import { useRef, useEffect, useMemo } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import {
  IconExpand,
  useQueryState,
  useGetList,
  useScroll,
  Table,
  usePrevious,
  IconLoader,
  useFilters,
  PermissionEnum,
  PermissionDenied,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  sourceOfFundsListSchema,
  sourceOfFundsListColumns,
  sourceOfFundsListFiltersSchema,
} from '../variables/source-of-funds';
import { useLazyGetSourceOfFundsListQuery } from '../store/services';
import {
  useLazyGetAmlDictionariesQuery,
  useLazyGetAmlBaseCurrenciesQuery,
  useLazyGetAmlCurrenciesQuery,
} from '../../../store/dictionaries/services';
import { RiskScoreItem, SourceOfFunds } from '../../types';
import ROUTES from '../../../routes';

import cn from '../individual-clients-risk.module.css';

const formatSourceOfFundsList = (list: SourceOfFunds[]): SourceOfFunds[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      riskCategory: (
        <div>{`${item.riskCategory.charAt(0)}${item.riskCategory
          .slice(1)
          .toLowerCase()} risk`}</div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const SourceOfFundsList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    individualClientsRisk: {
      sourceOfFunds: {
        list: { values, pagination },
      },
    },
    amlDictionaries: {
      activePermissions,
      riskScoreRanges,
      currencies,
      baseCurrency,
    },
  } = useAppSelector((state) => state);

  const [getDictionaries] = useLazyGetAmlDictionariesQuery();
  const [getAmlBaseCurrency] = useLazyGetAmlBaseCurrenciesQuery();
  const [getAmlCurrencies] = useLazyGetAmlCurrenciesQuery();

  const { sort, onSortChange, filters, onFilterChange } = useQueryState(
    sourceOfFundsListSchema,
  );

  const prevFilters = usePrevious(filters);

  const [getSourceOfFunds, { isLoading, isFetching }] =
    useLazyGetSourceOfFundsListQuery();

  const { getListAction, setPage, onSort, onFilter, errorList } =
    useGetList<RiskScoreItem>({
      getList: getSourceOfFunds,
      onFilterChange,
      onSortChange,
      schema: sourceOfFundsListSchema,
    });

  const formattedData = useMemo(() => {
    const data = formatSourceOfFundsList(values as SourceOfFunds[]);

    return data;
  }, [values, sort]);

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: sourceOfFundsListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !values &&
      !isLoading &&
      sort &&
      Object.keys(sort).length &&
      activePermissions?.includes(PermissionEnum.SETTINGS_SOURCES_OF_FUNDS_LIST)
    ) {
      getListAction({
        filters,
        sort,
      });
    }
  }, [filters, prevFilters, values, sort, activePermissions]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.SETTINGS_SOURCES_OF_FUNDS_LIST)
    ) {
      if (!riskScoreRanges) {
        getDictionaries();
      }

      if (!currencies) {
        getAmlCurrencies();
      }

      if (!baseCurrency) {
        getAmlBaseCurrency();
      }
    }
  }, [activePermissions, riskScoreRanges, currencies, baseCurrency]);

  useEffect(() => {
    let title = 'Source of funds';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;
  let globalContent = (
    <div className={cn.sourceOfFundsLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (
      activePermissions.includes(PermissionEnum.SETTINGS_SOURCES_OF_FUNDS_LIST)
    ) {
      if (isLoading) {
        content = (
          <div className={cn.sourceOfFundsLoader}>
            <IconLoader />
          </div>
        );
      } else {
        content = (
          <Table
            columns={sourceOfFundsListColumns}
            data={formattedData || []}
            onSort={(name) => onSort(name)}
            filters={filters}
            sort={sort}
            columnsLabels={{}}
            onFilterChange={(filtersValues) => onFilter(filtersValues)}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isFetching}
            take="30"
            error={errorList}
            setPage={(data) => setPage({ ...data, filters })}
            pagination={pagination}
            topContent={
              <div>
                {activePermissions?.includes(
                  PermissionEnum.SETTINGS_SOURCES_OF_FUNDS_CREATE_ACTION,
                ) && (
                  <NavLink
                    to={ROUTES.INDIVIDUAL_CLIENTS_RISK_SOURCE_OF_FUNDS_CREATE}
                    className={cn.sourceOfFundsLink}
                  >
                    <IconExpand />
                    Add Source
                  </NavLink>
                )}
              </div>
            }
            clickable
          />
        );
      }

      globalContent = (
        <div className={cn.sourceOfFunds}>
          <div className={cn.sourceOfFundsBottom}>
            <div className={cn.sourceOfFundsTableWrapper} ref={scrollRef}>
              {content}
            </div>
            <Outlet />
          </div>
        </div>
      );
    } else {
      globalContent = <PermissionDenied />;
    }
  }

  return globalContent;
};

export default SourceOfFundsList;
